import { useState, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes, faLock } from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import { workosAuth } from "@/services/api/workos";
import { Button } from "@/components/ui/button/Button";
import FormInput from "@/components/ui/form-input";
import { useNotification } from "@/context/NotificationContext";
import {
  isPasswordAcceptable,
  PASSWORD_POLICY_ERROR,
} from "@/security/passwordValidation";

export interface ChangePasswordDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

export function ChangePasswordDialog({
  isOpen,
  onClose,
}: ChangePasswordDialogProps) {
  const { showSuccess, showError } = useNotification();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const resetForm = useCallback(() => {
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setLocalError(null);
  }, []);

  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [onClose, resetForm]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);

    if (!isPasswordAcceptable(newPassword)) {
      setLocalError(PASSWORD_POLICY_ERROR);
      return;
    }
    if (newPassword === currentPassword) {
      setLocalError("New password must be different from the current password.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setLocalError("Passwords do not match.");
      return;
    }

    setSubmitting(true);
    try {
      await workosAuth.changePassword(currentPassword, newPassword);
      showSuccess("Password updated", "Use your new password the next time you sign in.");
      resetForm();
      onClose();
    } catch (err) {
      showError(
        "Could not change password",
        err instanceof Error ? err.message : "Could not update password.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
      data-testid="change-password-dialog-backdrop"
    >
      <div
        className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden"
        data-testid="change-password-dialog"
      >
        <div className="flex items-center justify-between p-6 border-b border-slate-200 dark:border-slate-700">
          <div className="flex items-center gap-2">
            <FontAwesomeIcon
              icon={faLock}
              className="text-slate-600 dark:text-slate-400"
            />
            <h2 className="text-xl font-bold text-slate-900 dark:text-white">
              Change password
            </h2>
          </div>
          <button
            type="button"
            onClick={handleClose}
            disabled={submitting}
            className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors disabled:opacity-50"
            data-testid="change-password-dialog-close"
            aria-label="Close"
          >
            <FontAwesomeIcon
              icon={faTimes}
              className="text-slate-600 dark:text-slate-400"
            />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <p className="text-sm text-slate-600 dark:text-slate-400">
            Enter your current password, then choose a new one.
          </p>

          {localError && (
            <div
              className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-red-600 dark:text-red-400"
              data-testid="change-password-dialog-error"
            >
              {localError}
            </div>
          )}

          <FormInput
            id="change-password-current"
            name="change-password-current"
            type="password"
            label="Current password"
            required
            autoComplete="current-password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            data-testid="change-password-current-input"
          />

          <FormInput
            id="change-password-new"
            name="change-password-new"
            type="password"
            label="New password"
            required
            autoComplete="new-password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            data-testid="change-password-new-input"
          />

          <FormInput
            id="change-password-confirm"
            name="change-password-confirm"
            type="password"
            label="Confirm new password"
            required
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            data-testid="change-password-confirm-input"
          />

          <div className="flex gap-3 pt-2">
            <Button
              type="button"
              outline
              className="flex-1"
              onClick={handleClose}
              disabled={submitting}
              data-testid="change-password-cancel"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              color="indigo"
              className="flex-1"
              loading={submitting}
              disabled={submitting}
              data-testid="change-password-submit"
            >
              Update password
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
